import { lazy, ReactElement } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';

import Loadable from 'components/Loadable';
import MainLayout from 'layout/MainLayout';
import useAuth from 'hooks/useAuth';

const Login = Loadable(lazy(() => import('pages/auth/login')));
const ForgotPassword = Loadable(lazy(() => import('pages/auth/forgot-password')));
const ResetPassword = Loadable(lazy(() => import('pages/auth/reset-password')));
const AcceptInvitation = Loadable(lazy(() => import('pages/auth/accept-invitation')));
const Register = Loadable(lazy(() => import('pages/auth/register')));
const Overview = Loadable(lazy(() => import('pages/partner/overview')));
const Customers = Loadable(lazy(() => import('pages/partner/customers')));
const Commissions = Loadable(lazy(() => import('pages/partner/commissions')));
const Statements = Loadable(lazy(() => import('pages/partner/statements')));
const StatementDetail = Loadable(lazy(() => import('pages/partner/statements/statementDetail')));
const Payouts = Loadable(lazy(() => import('pages/partner/payouts')));

// Protected pages are never painted (and never fire their requests) without a session.
const AuthGuard = ({ children }: { children: ReactElement }) => {
  const { isLoggedIn } = useAuth();
  const location = useLocation();
  if (!isLoggedIn) return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  return children;
};

export default function Routes() {
  return useRoutes([
    { path: 'login', element: <Login /> },
    { path: 'forgot-password', element: <ForgotPassword /> },
    { path: 'reset-password', element: <ResetPassword /> },
    { path: 'accept-invitation', element: <AcceptInvitation /> },
    { path: 'register', element: <Register /> },
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        { index: true, element: <Navigate to="/overview" replace /> },
        { path: 'overview', element: <Overview /> },
        { path: 'customers', element: <Customers /> },
        { path: 'commissions', element: <Commissions /> },
        { path: 'statements', element: <Statements /> },
        { path: 'statements/:period', element: <StatementDetail /> },
        { path: 'payouts', element: <Payouts /> },
        { path: '*', element: <Navigate to="/overview" replace /> }
      ]
    }
  ]);
}
